/**
 * Inventory module constants — single entry (webpack resolves ../../constants here).
 */
export const BASE_URL =
  process.env.REACT_APP_BACKEND_ROUTE_URL || 'http://localhost.dev.com:8000/app/api';

export const ITEM_TYPES = [
  'MEDICINE', 'CONSUMABLE', 'SURGICAL', 'IMPLANT',
  'ASSET', 'EQUIPMENT', 'REAGENT', 'LINEN', 'GENERAL', 'OTHER',
];

export const CATEGORY_TYPES = [
  'MEDICINE', 'CONSUMABLE', 'SURGICAL', 'IMPLANT', 'EQUIPMENT',
  'ASSET', 'REAGENT', 'LINEN', 'GENERAL', 'OTHER',
];

export const CATEGORY_PAGE_SIZE = 10;

export const MASTER_PAGE_SIZE = 10;

export const APPLICABLE_STORE_TYPES = [
  { value: 'PHARMACY', label: 'Pharmacy' },
  { value: 'CENTRAL_STORE', label: 'Central Store' },
];

export const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export const UOM_TYPES = [
  'COUNT', 'VOLUME', 'WEIGHT', 'LENGTH', 'PACK', 'OTHER',
];

export const UOM_PAGE_SIZE = 10;

export const UOM_NAMES_BY_TYPE = {
  COUNT: ['Each', 'Unit', 'Tablet', 'Capsule', 'Kit', 'Pair'],
  VOLUME: ['Millilitre', 'Litre', 'Microlitre'],
  WEIGHT: ['mcg', 'mg', 'g', 'kg'],
  LENGTH: ['mm', 'cm', 'm'],
  PACK: ['Strip', 'Box', 'Vial', 'Ampoule', 'Bottle', 'Carton', 'Packet'],
  OTHER: [],
};

export const UOM_PRESET_DEFAULTS = {
  'COUNT:Tablet':      { code: 'TAB' },
  'COUNT:Capsule':     { code: 'CAP' },
  'COUNT:Each':        { code: 'EA' },
  'COUNT:Unit':        { code: 'UN' },
  'VOLUME:Millilitre': { code: 'ML' },
  'VOLUME:Litre':      { code: 'L' },
  'PACK:Strip':        { code: 'STR' },
  'PACK:Box':          { code: 'BOX' },
  'PACK:Vial':         { code: 'VIAL' },
  'PACK:Ampoule':      { code: 'AMP' },
  'PACK:Bottle':       { code: 'BTL' },
};

export const VENDOR_TYPES = [
  { value: 'MANUFACTURER', label: 'Manufacturer' },
  { value: 'DISTRIBUTOR', label: 'Distributor' },
  { value: 'WHOLESALER', label: 'Wholesaler' },
  { value: 'IMPORTER', label: 'Importer' },
  { value: 'LOCAL_SUPPLIER', label: 'Local Supplier' },
  { value: 'AUTHORIZED_DEALER', label: 'Authorized Dealer' },
  { value: 'SERVICE_PROVIDER', label: 'Service Provider' },
  { value: 'PHARMACEUTICAL_SUPPLIER', label: 'Pharmaceutical Supplier' },
  { value: 'PHARMACY_VENDOR', label: 'Pharmacy Vendor' },
  { value: 'MEDICAL_EQUIPMENT_SUPPLIER', label: 'Medical Equipment Supplier' },
  { value: 'SURGICAL_CONSUMABLE_SUPPLIER', label: 'Surgical / Consumable Supplier' },
  { value: 'OTHER', label: 'Other' },
];

export const VENDOR_DOCUMENT_TYPES = [
  { key: 'PAN_CARD', label: 'PAN Card', showNumber: true, numberLabel: 'PAN Number' },
  { key: 'AADHAAR', label: 'Aadhaar Card', showNumber: true, numberLabel: 'Aadhaar Number' },
  { key: 'DRIVING_LICENSE', label: 'Driving License', showNumber: true, numberLabel: 'License Number' },
  { key: 'GST_CERTIFICATE', label: 'GST Certificate', showNumber: false },
  { key: 'DRUG_LICENSE_COPY', label: 'Drug License Copy', showNumber: false },
  { key: 'CANCELLED_CHEQUE', label: 'Cancelled Cheque', showNumber: false },
];

export const VENDOR_APPROVAL_STATUSES = [
  'DRAFT',
  'PENDING_APPROVAL',
  'APPROVED',
  'REJECTED',
  'SUSPENDED',
  'BLACKLISTED',
];

export const VENDOR_CURRENCIES = [
  { value: 'INR', label: 'INR' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'GBP', label: 'GBP' },
];

export const STORE_TYPES = [
  'MAIN_STORE', 'PHARMACY', 'WARD_STORE', 'OT_STORE',
  'ICU_STORE', 'DEPARTMENT_STORE', 'SUB_STORE', 'OTHER',
];

export const PRIORITY_LEVELS = ['LOW', 'NORMAL', 'HIGH', 'URGENT', 'EMERGENCY'];

// Dispensing: requisition → issue → return lifecycle.
export const REQUISITION_STATUSES = [
  'DRAFT', 'SUBMITTED', 'APPROVED', 'RESERVED',
  'PARTIALLY_ISSUED', 'ISSUED', 'REJECTED', 'CANCELLED',
];

// Statuses where stock may still be issued against a requisition.
export const REQUISITION_ISSUABLE_STATUSES = ['APPROVED', 'RESERVED', 'PARTIALLY_ISSUED'];

// Issue lifecycle: DRAFT → ISSUED (awaiting ack) → ACKNOWLEDGED → COMPLETED
export const ISSUE_STATUSES = ['DRAFT', 'ISSUED', 'ACKNOWLEDGED', 'COMPLETED'];

/** Human labels for issue StatusChip / filters (ISSUED ≠ completed). */
export const ISSUE_STATUS_LABELS = {
  DRAFT: 'Draft',
  ISSUED: 'Awaiting Acknowledgement',
  ACKNOWLEDGED: 'Acknowledged',
  COMPLETED: 'Completed',
};

/**
 * IPD medication order book (CPOE). These are the ONLY statuses the EMR backend
 * writes — `Sent -> Acknowledged -> Partial -> Dispensed -> Received` forward-only,
 * with `Cancel` and `ShortClosed` outside the chain (both terminal). Listed here in
 * normalized (upper-case) form; the raw values are title-case on the wire
 * ("Partial", "ShortClosed").
 *
 * Deliberately excludes ISSUED / PARTIALLY_ISSUED: those belong to the stock-issue
 * lifecycle and `/get_ipd_prescriptions/` can never return them.
 */
export const PRESCRIPTION_STATUSES = [
  'SENT',
  'ACKNOWLEDGED',
  'PARTIAL',
  'DISPENSED',
  'RECEIVED',
  'CANCEL',
  'SHORT_CLOSED',
];

/**
 * Display labels for prescription chips / filters. These deliberately read back the
 * backend's own vocabulary verbatim — a chip saying "Pending" while the API says
 * "Sent" made the screen impossible to reconcile against /get_ipd_prescriptions/.
 * The map exists only to restore the backend's casing: without it a chip would
 * render the normalized code ("ACKNOWLEDGED").
 *
 * ACTIVE is the normalizer's stand-in for a blank status, so it shares SENT's label.
 * The legacy partial states only occur in mock/demo row shapes.
 */
export const PRESCRIPTION_STATUS_LABELS = {
  ACTIVE: 'Sent',
  SENT: 'Sent',
  ACKNOWLEDGED: 'Acknowledged',
  PARTIAL: 'Partially Dispensed',
  DISPENSED: 'Dispensed',
  RECEIVED: 'Received',
  CANCEL: 'Cancel',
  SHORT_CLOSED: 'Closed (Short)',
  NOT_SUPPLIED: 'Not Supplied',
  PARTIALLY_ISSUED: 'Partially Issued',
  PARTIALLY_DISPENSED: 'Partially Dispensed',
};

/**
 * Reason vocabularies for the order-book terminal transitions. Both lists are the
 * backend's own (main/ipd/order_lifecycle.py PHARMACY_SHORT_CLOSE_REASONS /
 * CLINICAL_SHORT_CLOSE_REASONS) — the endpoint requires a reason for ShortClosed
 * and Cancel, and 'Other' carries free text in its place.
 */
export const PHARMACY_SHORT_CLOSE_REASONS = [
  'Out of Stock',
  'Batch Expired',
  'Not in Formulary',
  'Patient Left',
  'Other',
];

/** Closing a stale order posts Cancel — clinical vocabulary, not stock vocabulary. */
export const STALE_ORDER_CANCEL_REASONS = [
  'Therapy Stopped',
  'Patient Discharged',
  'Dose Changed',
  'Other',
];

/** Filter-dropdown labels for the ward-return inbox. Badges use the status code, same as PO. */
export const WARD_RETURN_STATUS_LABELS = {
  DRAFT: 'DRAFT',
  SUBMITTED: 'SUBMITTED',
  RETURNED: 'RETURNED',
  PARTIALLY_RETURNED: 'PARTIALLY RETURNED',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
};

export const APPROVAL_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'NOT_REQUIRED'];

export const ISSUE_TYPES = [
  { value: 'STORE_TO_DEPARTMENT', label: 'Store → Department' },
  { value: 'STORE_TO_WARD', label: 'Store → Ward' },
  { value: 'STORE_TO_SUBSTORE', label: 'Store → Sub-store' },
];

// Stock returns (distinct from the vendor RETURN_BEHAVIORS in itemClassification).
export const RETURN_TYPES = [
  { value: 'PATIENT_RETURN', label: 'Patient Return' },
  { value: 'WARD_RETURN', label: 'Ward Return' },
  { value: 'DEPARTMENT_RETURN', label: 'Department Return' },
  { value: 'PHARMACY_RETURN', label: 'Pharmacy Return' },
];

export const MOVEMENT_TYPES = [
  'RECEIPT', 'ISSUE', 'TRANSFER_IN', 'TRANSFER_OUT',
  'ADJUSTMENT', 'RESERVATION', 'CONSUMPTION', 'RETURN',
];

// Pharmacy billing: split-payment / refund-settle modes (SplitPaymentCollector).
export const PAYMENT_MODES = ['CASH', 'UPI', 'CARD', 'WALLET', 'CHEQUE'];

export const HOSPITAL_INFO = {
  name:          'HlthTek Healthcare',
  tagline:       'Hospital Management Platform',
  address:       'Your Hospital Address  ·  City  ·  State  ·  PIN',
  gstin:         '00AAAAA0000A1Z0',
  email:         'procurement@hlthtek.com',
  phone:         '+91-00000-00000',
  department:    'Procurement',
  qcDepartment:  'Stores Quality Cell',
  poTerms: [
    'Payment terms as stated herein; applicable from date of accepted GRN.',
    'Goods must match approved specifications; rejected lots returnable at vendor cost.',
    'Quote PO number on all invoices, delivery challans and packaging.',
    'Delivery to designated receiving store during 9:00–17:00 IST on working days.',
    'Disputes subject to applicable jurisdiction.',
  ],
  grnQcNotes: [
    'Quantities verified against purchase order and delivery challan.',
    'Batch number and expiry date recorded for all batch-tracked items.',
    'Cold-chain integrity confirmed for temperature-sensitive items.',
    'Rejected quantities shall be returned to vendor via debit note.',
  ],
};

export {
  BARCODE_TYPES,
  SUBSTITUTE_TYPES,
  RETURN_BEHAVIORS,
} from './itemClassification';

export {
  TAX_MASTER_PAGE_SIZE,
  GST_RATE_OPTIONS,
  GST_RATE_VALUES,
  normalizeGstRateForSelect,
  TAX_CATEGORY_OPTIONS,
} from './taxMaster';

export {
  GRN_KINDS,
  GRN_PAYMENT_TYPES,
  INDIAN_STATES,
} from './grnManagement';

export {
  INVOICE_STATUS_OPTIONS,
  DEFAULT_MATCH_TOLERANCE_PERCENT,
  PAYMENT_TERMS_OPTIONS,
  NET_DAYS_BY_PAYMENT_TERM,
} from './purchaseInvoiceManagement';

export {
  GATE_PASS_DIRECTION_LABELS,
  RETURNABLE_OPTIONS,
  GATE_PASS_PURPOSES,
  GATE_PASS_PURPOSE_LABELS,
  PURPOSE_DEFAULT_RETURNABLE,
  GATE_PASS_PARTY_TYPES,
  GATE_PASS_PARTY_TYPE_LABELS,
  GATE_PASS_STATUS,
  GATE_PASS_STATUS_LABELS,
  GATE_PASS_STAGES,
  GATE_PASS_STAGE_LABELS,
  GATE_PASS_STATUS_FILTER_OPTIONS,
} from './gatePass';
